import {
    IColumn,
    IDetailsGroupDividerProps,
    IDetailsListProps,
    IGroup,
    IRenderFunction,
    IShimmerElement,
    SelectionMode,
    ShimmeredDetailsList as FluentDetailsList,
    ShimmerElementType
} from "@fluentui/react";
import React, { ReactElement } from "react";

import { DefaultButton } from "../inputs/button/DefaultButton";
import { Text } from "../items/Text";
import { Stack } from "../layouts/stack/Stack";
import { StackItem } from "../layouts/stack/StackItem";
import { Shimmer } from "../progress/Shimmer";
import { Theme } from "../theme/Theme";
import { getLayoutMode, LayoutMode } from "./LayoutMode";
import { ListTableColumn } from "./ListTableColumn";
import { renderDefault } from "./renderFunctions";

export type ListGroup<T = Record<string, unknown>> = IGroup & T;

export type GroupRenderProps = IDetailsGroupDividerProps;

export interface GroupedDetailsListProps<T> extends Pick<IDetailsListProps, "items" | "groups"> {
    columns: ListTableColumn<T>[];
    totalItemsCount: number;
    isLoading: boolean;
    noItemsMessage?: string;
    onRenderGroupHeader?: IRenderFunction<GroupRenderProps>;
    hideEmptyGroups?: boolean;
    loadMoreProps?: {
        pageSize: number;
        isMoreLoading: boolean;
        onLoadMoreClick(): void;
    };
    layoutMode?: LayoutMode;
}

export const GroupedDetailsList = <T,>(props: GroupedDetailsListProps<T>): ReactElement | null => {
    const {
        items,
        totalItemsCount,
        groups,
        columns,
        isLoading,
        noItemsMessage,
        onRenderGroupHeader,
        hideEmptyGroups,
        loadMoreProps,
        layoutMode
    } = props;

    if (!isLoading && !items.length && !groups?.length && !hideEmptyGroups) {
        return (
            <Stack horizontalAlign="center" styles={{ root: { padding: 10 } }}>
                <Text>{noItemsMessage || "No data available"}</Text>
            </Stack>
        );
    }

    const handleLoadMoreClick = () => {
        if (loadMoreProps) {
            loadMoreProps.onLoadMoreClick();
        }
    };

    const getShimmerElements = (columnsToShimmer?: IColumn[]): IShimmerElement[] => {
        if (!columnsToShimmer?.length) {
            throw new Error("For shimmer to render 'columns' must not be empty");
        }

        return columnsToShimmer
            .map((column: IColumn): IShimmerElement[] => [
                { type: ShimmerElementType.gap, width: 30, height: 42 },
                { type: ShimmerElementType.line, width: column.maxWidth, height: 7 }
            ])
            .reduce((a: IShimmerElement[], b: IShimmerElement[]) => a.concat(b))
            .concat([{ type: ShimmerElementType.gap, width: "100%" }]);
    };

    const emptyGroups = groups?.filter((group: IGroup) => group.count === 0) || [];

    const emptyGroupsCount: number = hideEmptyGroups ? 0 : emptyGroups.length;

    const showingCount = (items.length as number) + emptyGroupsCount;

    const isLoadMoreDisabled = showingCount >= totalItemsCount || loadMoreProps?.isMoreLoading;

    const listLayoutMode = getLayoutMode(layoutMode);

    return (
        <Stack>
            <FluentDetailsList
                items={items}
                groups={groups?.length ? groups : undefined}
                groupProps={{ showEmptyGroups: !hideEmptyGroups, onRenderHeader: onRenderGroupHeader }}
                columns={columns}
                selectionMode={SelectionMode.none}
                enableShimmer={isLoading}
                onRenderItemColumn={renderDefault}
                layoutMode={listLayoutMode}
            />
            {loadMoreProps?.isMoreLoading &&
                columns?.length &&
                new Array(loadMoreProps.pageSize).fill(
                    <Shimmer
                        shimmerElements={getShimmerElements(columns)}
                        styles={{ shimmerWrapper: { backgroundColor: Theme.palette.neutralLighterAlt } }}
                    />
                )}
            <StackItem styles={{ root: { textAlign: "left", padding: 10 } }}>{`Showing ${showingCount || "-"} / ${
                totalItemsCount || "-"
            }`}</StackItem>
            {loadMoreProps && (
                <StackItem styles={{ root: { textAlign: "center", paddingTop: 10 } }}>
                    <DefaultButton
                        text="Load more"
                        onClick={handleLoadMoreClick}
                        styles={{ root: { width: 200 } }}
                        disabled={isLoadMoreDisabled}
                    />
                </StackItem>
            )}
        </Stack>
    );
};
